/**
 * KioskProductionBoard - Enhanced Shop Floor Display
 * 
 * Features:
 * - No login required (uses kiosk access key in URL)
 * - Auto-refreshes every 30 seconds
 * - Summary bar with urgency counts + throughput
 * - Color-coded urgency (Overdue/Today/This Week)
 * - Time in stage indicator
 * - Operator names on active jobs
 * - Collapsible empty stages
 * - Stage capacity warnings
 * 
 * Usage: /kiosk/production?key=YOUR_KIOSK_KEY
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  RefreshCw, Maximize2, Minimize2, AlertTriangle, Play, Wifi, WifiOff,
  Clock, User, AlertCircle, Calendar, CalendarClock, ChevronDown, ChevronRight,
  TrendingUp, Package, Timer
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KioskProductionBoard = () => {
  const [jobs, setJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [collapsedColumns, setCollapsedColumns] = useState(new Set());

  // Get kiosk key from URL
  const urlParams = new URLSearchParams(window.location.search);
  const kioskKey = urlParams.get('key') || '';

  // Overscan compensation for TVs that crop the edges (Raspberry Pi over HDMI).
  // Tune with ?overscan=N (percent per side, 0-10). Default 3%.
  const overscanParam = parseFloat(urlParams.get('overscan'));
  const overscan = Number.isFinite(overscanParam) ? Math.max(0, Math.min(10, overscanParam)) : 3;

  const columns = [
    { id: 'artwork', title: 'ARTWORK', color: 'bg-purple-600', borderColor: 'border-purple-500', capacity: 15 },
    { id: 'prepress', title: 'PREPRESS', color: 'bg-blue-600', borderColor: 'border-blue-500', capacity: 20 },
    { id: 'digital-print', title: 'DIGITAL', color: 'bg-cyan-600', borderColor: 'border-cyan-500', capacity: 15 },
    { id: 'litho-print', title: 'LITHO', color: 'bg-green-600', borderColor: 'border-green-500', capacity: 10 },
    { id: 'wide-format', title: 'WIDE FORMAT', color: 'bg-orange-600', borderColor: 'border-orange-500', capacity: 12 },
    { id: 'finishing', title: 'FINISHING', color: 'bg-pink-600', borderColor: 'border-pink-500', capacity: 20 },
    { id: 'packaging', title: 'PACKAGING', color: 'bg-amber-600', borderColor: 'border-amber-500', capacity: 15 },
    { id: 'dispatch', title: 'DISPATCH', color: 'bg-teal-600', borderColor: 'border-teal-500', capacity: 25 },
  ];

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Toggle column collapse
  const toggleColumnCollapse = (columnId) => {
    setCollapsedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      return newSet;
    });
  };

  // Auto-enter fullscreen on load
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    setTimeout(() => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }, 1000);
    
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Fetch jobs from kiosk API
  const fetchJobs = useCallback(async () => {
    if (!kioskKey) {
      setError('Missing kiosk access key. Add ?key=YOUR_KEY to URL');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/kiosk/jobs?key=${kioskKey}&limit=300`);
      setJobs(response.data.jobs || []);
      setIsConnected(true);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setIsConnected(false);
      if (err.response?.status === 401) {
        setError('Invalid kiosk access key');
      }
    } finally {
      setLoading(false);
    }
  }, [kioskKey]);

  // Fetch active job tracking with operator info
  const fetchActiveJobs = useCallback(async () => {
    if (!kioskKey) return;

    try {
      const response = await axios.get(`${API}/kiosk/active-tracking?key=${kioskKey}`);
      setActiveJobs(response.data.active_jobs || []);
      setIsConnected(true);
    } catch (err) {
      console.error('Failed to fetch active jobs:', err);
      setIsConnected(false);
    }
  }, [kioskKey]);

  // Auto-refresh
  useEffect(() => {
    fetchJobs();
    fetchActiveJobs();
    
    const jobsInterval = setInterval(fetchJobs, 30000);
    const activeInterval = setInterval(fetchActiveJobs, 5000);
    
    return () => {
      clearInterval(jobsInterval);
      clearInterval(activeInterval);
    };
  }, [fetchJobs, fetchActiveJobs]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get active job info including operator name
  const getActiveJobInfo = (jobId) => {
    return activeJobs.find(aj => aj.job_id === jobId);
  };

  // Check job urgency level
  const getJobUrgency = (job) => {
    if (!job.date_due || job.date_due === '0000-00-00') return 'none';
    
    const dueDate = new Date(job.date_due);
    dueDate.setHours(23, 59, 59, 999);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + 7);
    
    if (dueDate < today) return 'overdue';
    if (dueDate < tomorrow) return 'today';
    if (dueDate < endOfWeek) return 'this-week';
    return 'future';
  };

  // Calculate time in stage
  const getTimeInStage = (job) => {
    const stageTime = job.stage_changed_at || job.last_synced_at || job.created_at;
    if (!stageTime) return null;
    
    const now = new Date();
    const stageDate = new Date(stageTime);
    const diffMs = now - stageDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return { value: diffDays, unit: 'd', isStuck: diffDays >= 1 };
    if (diffHours > 0) return { value: diffHours, unit: 'h', isStuck: diffHours >= 24 };
    return { value: Math.floor(diffMs / (1000 * 60)), unit: 'm', isStuck: false };
  };

  // Group jobs by column
  const getJobsByColumn = (columnId) => {
    return jobs
      .filter(job => job.board_stage === columnId && job.visible_on_board !== false)
      .sort((a, b) => {
        const dateA = a.date_due && a.date_due !== '0000-00-00' ? new Date(a.date_due) : new Date('9999-12-31');
        const dateB = b.date_due && b.date_due !== '0000-00-00' ? new Date(b.date_due) : new Date('9999-12-31');
        return dateA - dateB;
      });
  };

  // Calculate summary counts
  const getSummaryCounts = () => {
    const counts = { overdue: 0, today: 0, thisWeek: 0, total: jobs.length, activeOperators: new Set() };
    
    jobs.forEach(job => {
      const urgency = getJobUrgency(job);
      if (urgency === 'overdue') counts.overdue++;
      else if (urgency === 'today') counts.today++;
      else if (urgency === 'this-week') counts.thisWeek++;
    });
    
    activeJobs.forEach(aj => {
      if (aj.operator_name) counts.activeOperators.add(aj.operator_name);
    });
    
    return counts;
  };

  // Dynamic sizing based on job count
  const getJobCardStyle = (jobCount) => {
    if (jobCount <= 5) return { padding: 'p-2', text: 'text-xs', title: 'text-sm font-bold' };
    if (jobCount <= 10) return { padding: 'p-1.5', text: 'text-[10px]', title: 'text-xs font-bold' };
    return { padding: 'p-1', text: 'text-[10px]', title: 'text-[10px] font-bold' };
  };

  // Error screen
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Kiosk Error</h1>
          <p className="text-xl text-red-500 mb-6">{error}</p>
          <p className="text-gray-500">
            URL format: /kiosk/production?key=YOUR_KIOSK_KEY
          </p>
        </div>
      </div>
    );
  }

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-cyan-600 animate-spin mx-auto mb-4" />
          <p className="text-2xl font-bold text-gray-800">Loading Production Board...</p>
          <p className="text-gray-500 mt-2">Kiosk Mode</p>
        </div>
      </div>
    );
  }

  const counts = getSummaryCounts();

  return (
    <div
      className="h-screen bg-gray-100 flex flex-col overflow-hidden"
      style={{ paddingTop: `${overscan}vh`, paddingBottom: `${overscan}vh`, paddingLeft: `${overscan}vw`, paddingRight: `${overscan}vw` }}
    >
      {/* Enhanced Header with Summary Bar */}
      <header className="flex-shrink-0 px-4 py-2 bg-white border-b border-gray-300 shadow-sm">
        <div className="flex items-center justify-between">
          {/* Title */}
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-black text-gray-800 tracking-tight">
              PRINTLOGIC <span className="text-cyan-600">PRODUCTION</span>
            </h1>
            {/* Connection indicator */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded ${isConnected ? 'bg-green-100' : 'bg-red-100'}`}>
              {isConnected ? (
                <Wifi className="w-3 h-3 text-green-600" />
              ) : (
                <WifiOff className="w-3 h-3 text-red-600 animate-pulse" />
              )}
              <span className={`text-[10px] font-bold ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
                {isConnected ? 'LIVE' : 'OFFLINE'}
              </span>
            </div>
          </div>

          {/* Summary Counts */}
          <div className="flex items-center gap-2">
            {counts.overdue > 0 && (
              <div className="flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded animate-pulse">
                <AlertCircle className="w-3 h-3" />
                <span className="text-sm font-bold">{counts.overdue}</span>
                <span className="text-[10px]">Overdue</span>
              </div>
            )}
            {counts.today > 0 && (
              <div className="flex items-center gap-1 bg-amber-500 text-white px-2 py-1 rounded">
                <Calendar className="w-3 h-3" />
                <span className="text-sm font-bold">{counts.today}</span>
                <span className="text-[10px]">Today</span>
              </div>
            )}
            {counts.thisWeek > 0 && (
              <div className="flex items-center gap-1 bg-orange-500 text-white px-2 py-1 rounded">
                <CalendarClock className="w-3 h-3" />
                <span className="text-sm font-bold">{counts.thisWeek}</span>
                <span className="text-[10px]">This Week</span>
              </div>
            )}
            <div className="flex items-center gap-1 bg-gray-600 text-white px-2 py-1 rounded">
              <Package className="w-3 h-3" />
              <span className="text-sm font-bold">{counts.total}</span>
              <span className="text-[10px]">Total</span>
            </div>
            {counts.activeOperators.size > 0 && (
              <div className="flex items-center gap-1 bg-green-600 text-white px-2 py-1 rounded">
                <User className="w-3 h-3" />
                <span className="text-sm font-bold">{counts.activeOperators.size}</span>
                <span className="text-[10px]">Working</span>
              </div>
            )}
          </div>

          {/* Time and Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleFullscreen}
              className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              {isFullscreen ? (
                <Minimize2 className="w-4 h-4 text-gray-600" />
              ) : (
                <Maximize2 className="w-4 h-4 text-gray-600" />
              )}
            </button>
            <div className="text-2xl font-bold text-gray-800 font-mono">
              {currentTime.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </div>
          </div>
        </div>
      </header>

      {/* Columns Grid */}
      <div className="flex-1 flex gap-1 p-2 overflow-hidden">
        {columns.map((column) => {
          const columnJobs = getJobsByColumn(column.id);
          const styles = getJobCardStyle(columnJobs.length);
          const isCollapsed = collapsedColumns.has(column.id);
          const isOverCapacity = columnJobs.length > column.capacity;
          const isEmpty = columnJobs.length === 0;
          
          return (
            <div 
              key={column.id} 
              className={`flex flex-col min-h-0 transition-all duration-300 ${
                isCollapsed ? 'w-10' : isEmpty ? 'w-16' : 'flex-1'
              }`}
            >
              {/* Column Header */}
              <div 
                className={`${column.color} px-2 py-1.5 rounded-t-lg flex-shrink-0 cursor-pointer ${
                  isOverCapacity ? 'ring-2 ring-red-500 animate-pulse' : ''
                }`}
                onClick={() => isEmpty && toggleColumnCollapse(column.id)}
              >
                <div className="flex items-center justify-between gap-1">
                  {!isCollapsed && (
                    <h2 className="text-[10px] font-black text-white uppercase tracking-wide truncate">
                      {column.title}
                    </h2>
                  )}
                  <div className="flex items-center gap-1">
                    {isOverCapacity && (
                      <AlertTriangle className="w-3 h-3 text-yellow-300" />
                    )}
                    <span className={`text-white text-xs font-bold px-1.5 py-0.5 rounded ${
                      isOverCapacity ? 'bg-red-500' : 'bg-white/20'
                    }`}>
                      {columnJobs.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Jobs List */}
              {!isCollapsed && (
                <div className={`flex-1 bg-white border-x-2 border-b-2 ${column.borderColor} rounded-b-lg overflow-y-auto`}>
                  <div className="p-1 space-y-1">
                    {columnJobs.map((job) => {
                      const urgency = getJobUrgency(job);
                      const activeInfo = getActiveJobInfo(job.job_id);
                      const isActive = !!activeInfo;
                      const timeInStage = getTimeInStage(job);
                      
                      // Urgency-based background colors (light theme)
                      let bgColor = 'bg-gray-50 border-gray-200';
                      let textColor = 'text-cyan-700';
                      
                      if (isActive) {
                        bgColor = 'bg-green-50 border-green-400 shadow-md shadow-green-200';
                        textColor = 'text-green-700';
                      } else if (urgency === 'overdue') {
                        bgColor = 'bg-red-50 border-red-300';
                        textColor = 'text-red-600';
                      } else if (urgency === 'today') {
                        bgColor = 'bg-amber-50 border-amber-300';
                        textColor = 'text-amber-600';
                      } else if (urgency === 'this-week') {
                        bgColor = 'bg-orange-50 border-orange-300';
                        textColor = 'text-orange-600';
                      }
                      
                      return (
                        <div
                          key={job.job_id}
                          className={`${styles.padding} rounded border transition-all ${bgColor}`}
                          style={isActive ? { animation: 'pulse-green 2s ease-in-out infinite' } : {}}
                        >
                          {/* Active Operator Banner */}
                          {isActive && activeInfo.operator_name && (
                            <div className="flex items-center gap-1 text-[9px] text-green-700 mb-1 bg-green-100 px-1 py-0.5 rounded">
                              <User className="w-2.5 h-2.5" />
                              <span className="truncate font-medium">{activeInfo.operator_name}</span>
                            </div>
                          )}
                          
                          {/* Urgency Badge */}
                          {!isActive && urgency !== 'none' && urgency !== 'future' && (
                            <div className={`text-[8px] font-black px-1 py-0.5 rounded mb-1 inline-block ${
                              urgency === 'overdue' ? 'bg-red-500 text-white' :
                              urgency === 'today' ? 'bg-amber-500 text-white' :
                              'bg-orange-500 text-white'
                            }`}>
                              {urgency === 'overdue' ? 'OVERDUE' : urgency === 'today' ? 'TODAY' : 'THIS WEEK'}
                            </div>
                          )}
                          
                          <div className="flex items-start justify-between gap-1">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1">
                                <p className={`${styles.title} ${textColor} truncate`}>
                                  #{job.job_id}
                                </p>
                                {isActive && <Play className="w-2.5 h-2.5 text-green-400 animate-pulse flex-shrink-0" />}
                              </div>
                              <p className={`${styles.text} text-gray-700 truncate`}>
                                {job.customer || 'Customer'}
                              </p>
                              {job.product && (
                                <p className={`${styles.text} text-gray-500 truncate`}>
                                  {job.product}
                                </p>
                              )}
                            </div>
                            
                            {/* Time in stage / Due date */}
                            <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                              {job.date_due && job.date_due !== '0000-00-00' && (
                                <div className={`flex items-center gap-0.5 ${styles.text} ${
                                  urgency === 'overdue' ? 'text-red-600' : 
                                  urgency === 'today' ? 'text-amber-600' : 'text-gray-500'
                                }`}>
                                  <Clock className="w-2.5 h-2.5" />
                                  <span>{new Date(job.date_due).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}</span>
                                </div>
                              )}
                              {timeInStage && (
                                <div className={`flex items-center gap-0.5 ${styles.text} ${
                                  timeInStage.isStuck ? 'text-yellow-600' : 'text-gray-400'
                                }`}>
                                  <Timer className="w-2.5 h-2.5" />
                                  <span>{timeInStage.value}{timeInStage.unit}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {columnJobs.length === 0 && (
                      <div className="text-center py-4 text-gray-400 text-xs">
                        No jobs
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Collapsed state */}
              {isCollapsed && (
                <div 
                  className={`flex-1 bg-white border-x-2 border-b-2 ${column.borderColor} rounded-b-lg flex items-center justify-center cursor-pointer`}
                  onClick={() => toggleColumnCollapse(column.id)}
                >
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="flex-shrink-0 px-4 py-1 bg-white border-t border-gray-300 flex items-center justify-between">
        <p className="text-gray-500 text-xs">
          KIOSK MODE • Auto-refreshes every 30 seconds
        </p>
        <div className="flex items-center gap-4">
          {activeJobs.length > 0 && (
            <p className="text-green-600 text-xs flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {activeJobs.length} jobs in progress
            </p>
          )}
          {lastUpdate && (
            <p className="text-gray-500 text-xs">
              Updated: {lastUpdate.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      </footer>

      {/* CSS for green pulse animation */}
      <style>{`
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 5px rgba(34, 197, 94, 0.3); }
          50% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.6); }
        }
      `}</style>
    </div>
  );
};

export default KioskProductionBoard;
